<template>
  <div :class="['input-editor-container', { label }]">
    <EditorContent :editor="viewer" spellcheck="false" />
    <FriedSpinner v-if="isUpdating" class="icon" :size="20" />
    <FriedIcon v-else-if="isError" icon="alert-circle" class="icon red" />
    <FriedIcon v-else-if="isSuccess" icon="check-circle" class="icon green" />
  </div>
</template>
<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core';
import { Editor as TipTapEditor, EditorContent } from '@tiptap/vue-3';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Superscript from '@tiptap/extension-superscript';
import History from '@tiptap/extension-history';
import Focus from '@tiptap/extension-focus';
import Text from '@tiptap/extension-text';
import type { Field, MergeValues } from '@getaccept/editor-lib';
import {
  ProsemirrorHelper,
  EnterBreakNode,
  InputDocNode,
  MergeValueNode,
  ParagraphNode,
} from '@getaccept/editor-lib';
import type { ShallowRef } from 'vue';
import { onBeforeUnmount, onMounted, computed, ref, watch } from 'vue';
import type { TextContentDoc } from '@getaccept/lib-shared/src/types/text-content';
import { updateMergeValues, setContent, handleInit } from './helpers/prosemirror-viewer.helper';

const {
  content = '',
  multiLine = true,
  bold = true,
  italic = true,
  underline = true,
  superscript = true,
  label = true,
  link = true,
  fieldFocused,
  editable,
  isUpdating,
  isError,
  isSuccess,
  mergeValues,
  fields = [],
  id,
  ariaLabelledby,
  ariaDescribedby,
  required,
} = defineProps<{
  content?: TextContentDoc | string;
  multiLine?: boolean;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  superscript?: boolean;
  label?: boolean;
  link?: boolean;
  fieldFocused?: boolean;
  editable?: boolean;
  isUpdating?: boolean;
  isError?: boolean;
  isSuccess?: boolean;
  mergeValues: MergeValues;
  fields?: Field[];
  id?: string;
  ariaLabelledby?: string;
  ariaDescribedby?: string;
  required?: boolean;
}>();

const emit = defineEmits<{
  update: [string];
}>();

const viewer = ref<ShallowRef<TipTapEditor> | undefined>(undefined);

const contentPayload = computed(() => ({
  viewer: viewer.value,
  content,
  fields,
  mergeValues,
}));

const innerTextContent = computed(() => {
  let transaction = viewer.value?.state.tr;
  if (!transaction || !viewer.value?.state.schema) {
    return '';
  }

  transaction = ProsemirrorHelper.replaceBrNodes(transaction, viewer.value?.state.schema);

  return ProsemirrorHelper.getStringFromTransaction(transaction, viewer.value?.state.schema);
});

const update = () => {
  if (!editable) {
    return;
  }

  emit('update', innerTextContent.value);
};

watch(
  () => editable,
  () => {
    viewer.value?.setEditable(editable);
  }
);

const debounceUpdate = useDebounceFn(update, 300);

const inputViewer = computed(() => ({
  content,
  editable,
  extensions: [
    InputDocNode,
    Focus,
    ParagraphNode,
    Text,
    MergeValueNode,
    History,
    ...(bold ? [Bold] : []),
    ...(italic ? [Italic] : []),
    ...(underline ? [Underline] : []),
    ...(superscript ? [Superscript] : []),
    ...(multiLine ? [EnterBreakNode] : []),
    ...(link
      ? [
          Link.configure({
            autolink: false,
            openOnClick: true,
            linkOnPaste: false,
          }),
        ]
      : []),
  ],
  onBlur: update,
  onUpdate: debounceUpdate,
  onCreate: () => handleInit(viewer.value, fields, mergeValues),
  enableInputRules: false,
  enablePasteRules: false,
  editorProps: {
    attributes: {
      role: editable ? 'textbox' : 'presentation',
      ...(id && { id }),
      ...(editable && ariaLabelledby && { 'aria-labelledby': ariaLabelledby }),
      ...(editable && ariaDescribedby && { 'aria-describedby': ariaDescribedby }),
      ...(editable && required && { 'aria-required': 'true' }),
    },
  },
}));

onMounted(() => {
  viewer.value = new TipTapEditor(inputViewer.value);
});

onBeforeUnmount(() => {
  viewer.value?.destroy();
});

watch(
  () => fields,
  () => {
    if (!viewer.value?.isDestroyed) {
      updateMergeValues(contentPayload.value);
    }
  }
);

watch(
  () => mergeValues,
  (newValues, oldValues) => {
    if (!viewer.value?.isDestroyed || JSON.stringify(newValues) === JSON.stringify(oldValues)) {
      return;
    }

    updateMergeValues(contentPayload.value);
  }
);

watch(
  () => content,
  () => {
    setContent(contentPayload.value);
  }
);

watch(
  () => fieldFocused,
  fieldFocused => {
    if (fieldFocused) {
      viewer.value?.view.focus();
    }
  }
);
</script>
<style lang="scss" scoped>
@import '@getaccept/editor-lib/src/scss/form-input-editor';

.icon {
  position: absolute;
  right: var(--spacing-50);
  top: calc(#{var(--spacing-50)} + 1px);
  width: 20px;

  &.red {
    color: var(--error-dark);
  }

  &.green {
    color: var(--success-dark);
  }
}
</style>
